/* eslint-disable max-lines */
import { bytesToNDigits } from '@mail/cross-sizes-utils';
import { Icon20CloudOutline } from '@vkontakte/icons';
import { Caption, Spacing, Title } from '@vkontakte/vkui';
import classNames from 'clsx';
import tariffPersonal from 'img/quota-landing/tariff-personal.png';
import tariffPersonal256 from 'img/quota-landing/tariff-personal-256.png';
import tariffStudy from 'img/quota-landing/tariff-study.png';
import tariffWork from 'img/quota-landing/tariff-work.png';
import { once } from 'ramda';
import React, { type ReactEventHandler, memo, useCallback, useEffect, useMemo } from 'react';
import { useSelector } from 'react-redux';
import { emitAnalyticEvent } from 'reactApp/appHelpers/experimentAnalytic';
import { AnalyticEventNames } from 'reactApp/appHelpers/experimentAnalytic/eventNames';
import {
    crossedFullPriceInTariffCard,
    desktopTariffMinSize,
    isRebrandingQuotaLanding,
    moreBenefitsInExpensiveTariffs,
} from 'reactApp/appHelpers/featuresHelpers';
import { AUTOUPLOAD_SHORT_FEATURE_TEXT, autoUploadPromo } from 'reactApp/appHelpers/featuresHelpers/features/autoUploadPromo';
import { autumnPromo } from 'reactApp/appHelpers/featuresHelpers/features/autumnPromo';
import { blackFriday2024 } from 'reactApp/appHelpers/featuresHelpers/features/blackFriday2024';
import { cheapTariffOnlyYearDesktop } from 'reactApp/appHelpers/featuresHelpers/features/cheapTariffOnlyYear';
import { compactFirstScroll } from 'reactApp/appHelpers/featuresHelpers/features/compactFirstScroll';
import { cyberMonday2025 } from 'reactApp/appHelpers/featuresHelpers/features/cyberMonday2025';
import {
    enabledFamilyTariff,
    familyTariffIds,
    familyTariffOnLandingA,
    familyTariffOnLandingB,
    familyTariffOnLandingC,
    familyTariffOnLandingD,
} from 'reactApp/appHelpers/featuresHelpers/features/familyTariff';
import { enabledExpensivePlanTrial, expensivePlanTariffIds } from 'reactApp/appHelpers/featuresHelpers/features/forceTrial';
import { isPromoQuotaWithLitress } from 'reactApp/appHelpers/featuresHelpers/features/litres';
import { mayPromo2025YearProduct, mayPromo2025YearProductId } from 'reactApp/appHelpers/featuresHelpers/features/mayPromo2025';
import { showEditingDocumentFeatureB } from 'reactApp/appHelpers/featuresHelpers/features/showEditingDocumentFeature';
import { tariffCardPersonal } from 'reactApp/appHelpers/featuresHelpers/features/tariifCardPersonal';
import { upsaleBonus } from 'reactApp/appHelpers/featuresHelpers/features/upsaleBonus';
import { upsaleWithTrial } from 'reactApp/appHelpers/featuresHelpers/features/upsaleWithTrial';
import { checkAdditionalBenefitsInSpace } from 'reactApp/constants/products/benefits.bySpace';
import { getAdditionalBenefitsCountText } from 'reactApp/constants/products/quotaFeatures';
import { SPACE_LIST } from 'reactApp/constants/spaceList';
import { useMergeRefs } from 'reactApp/hooks/useMergeRefs';
import { EnvironmentSelectors } from 'reactApp/modules/environment/environment';
import { getPaySource } from 'reactApp/modules/payment/payment.analytics';
import { openPopupHelper } from 'reactApp/modules/popup/popup.helpers';
import { popupNames } from 'reactApp/modules/popup/popup.types';
import { UserSelectors } from 'reactApp/modules/user/user.selectors';
import { UserQuotaSelectors } from 'reactApp/modules/userQuota/userQuota.selectors';
import { AutouploadTooltip } from 'reactApp/sections/QuotaLanding/AutouploadTooltip/AutouploadTooltip';
import { useTooltip } from 'reactApp/sections/QuotaLanding/AutouploadTooltip/AutouploadTooltip.hooks';
import { MODAL_TARIFF_BENEFITS_PAY_SOURCE } from 'reactApp/sections/QuotaLanding/DetailedTariffBenefitsPopup/DetailedTariffBenefitsPopup.constants';
import { useFamilyTariffFromQuery } from 'reactApp/sections/QuotaLanding/hooks/useFamilyTariffFromQuery';
import { useIntersecObsrWithParams } from 'reactApp/sections/QuotaLanding/hooks/useIntersecObsrWithParams';
import { useLitresTooltip } from 'reactApp/sections/QuotaLanding/LitresTooltip/hooks/useLitresTooltip';
import { LITRES_TOOLTIP_ID, LitresTooltip } from 'reactApp/sections/QuotaLanding/LitresTooltip/LitresTooltip';
import { sendQuotaGa } from 'reactApp/sections/QuotaLanding/QuotaLanding.helpers';
import { sendClickAnalytics, useBuyClick } from 'reactApp/sections/QuotaLanding/TariffsPlans/TariffPlansCard/useBuyClick';
import { renderUpsaleBonusModal } from 'reactApp/sections/QuotaLanding/UpsaleBonusDialog/UpsaleBonusDialog.helpers';
import type { Product } from 'reactApp/types/Billing';
import { ButtonLink } from 'reactApp/ui/ButtonLink/ButtonLink';
import { TariffCard } from 'reactApp/ui/TariffCardNew/TariffCard';
import { ETariffCardSizeButtonSize, ETariffCardWidth } from 'reactApp/ui/TariffCardNew/TariffCard.types';
import { useButtonAdditionalText } from 'reactApp/ui/TariffSectionNew/hooks/useButtonAdditionalText';
import { getFeaturesBySpace, useFeatures as getFeatures } from 'reactApp/ui/TariffSectionNew/hooks/useFeatures';
import { usePrice } from 'reactApp/ui/TariffSectionNew/hooks/usePrice';
import { useSwitchProducts } from 'reactApp/ui/TariffSectionNew/hooks/useSwitchProducts';
import { useTariffLabel } from 'reactApp/ui/TariffSectionNew/hooks/useTariffLabel';
import { ETabsName } from 'reactApp/ui/TariffSectionNew/TariffSection.constants';
import { noopVoid } from 'reactApp/utils/helpers';
import { isMonthPeriod, isYearPeriod } from 'reactApp/utils/Period';
import { sendTmrGoal, TMR3473039 } from 'reactApp/utils/tmr';

import { useTariffBorder } from './hooks/useTariffBorder';
import styles from './TariffPlansCard.css';
import type { Props } from './TariffPlansCard.types';

export const TariffInfoBlock = ({
    newQuota,
    isBase,
    isCheapest = false,
    isMobile,
    isSkeleton = false,
}: {
    newQuota: number;
    isBase?: boolean;
    isMobile?: boolean;
    isSkeleton?: boolean;
    isCheapest?: boolean;
}) => {
    // tempexp_16800-next_line
    let size = moreBenefitsInExpensiveTariffs === 'd' ? 12 : 24;
    size = isRebrandingQuotaLanding ? 36 : isCheapest ? 18 : size;
    if (compactFirstScroll) {
        size = 26;
    }
    if (showEditingDocumentFeatureB) {
        size = 16;
    }
    return (
        <>
            {!isMobile && <Spacing size={size} />}
            {isCheapest ? (
                <Title level="1" weight="2">
                    Без рекламы
                </Title>
            ) : isBase ? (
                <Spacing size={isRebrandingQuotaLanding ? 36 : 32} />
            ) : (
                <div className={classNames(styles.infoBlock, { [styles.infoBlock_rebranding]: isRebrandingQuotaLanding })}>
                    {isSkeleton ? (
                        <div className={styles.skeleton} />
                    ) : (
                        <>
                            <Icon20CloudOutline />{' '}
                            <Caption
                                level={'1'}
                                weight={isRebrandingQuotaLanding ? '3' : '2'}
                                className={classNames({
                                    [styles.caption_rebranding]: isRebrandingQuotaLanding,
                                    [styles.caption_rebrandingMobile]: isRebrandingQuotaLanding && isMobile,
                                })}
                            >
                                {`${isRebrandingQuotaLanding ? 'Общий объём составит' : 'Ваш объём составит'} ${
                                    bytesToNDigits(newQuota, 3).value
                                }`}
                            </Caption>
                        </>
                    )}
                </div>
            )}
            {isMobile && <Spacing size={isRebrandingQuotaLanding ? 24 : 30} />}
        </>
    );
};

export const TariffPlansCard = memo<Props>(
    ({
        tariff,
        isVisible = true,
        isIOSPromo = false,
        isAndroidPromo = false,
        setCarouselLoaded,
        isMobilePayAvailable,
        isMobilePayFromGetParam,
        activeTab,
        onTariffClick,
        // tempexp_18086-next-line
        isReferral,
        isMultiTariff,
        isHideButtonInMonthTariff,
        mayPromo2025Enabled,
    }) => {
        const { isLoaded: isQuotaLoaded } = useSelector(UserQuotaSelectors.getLoadingState);
        const { tariffId, products, isBase, isCheapest, isRecommended, tariffPromoLabelType, isMultiBasic, isMultiAdvanced } = tariff;
        const userQuota = useSelector(UserQuotaSelectors.getTotalOrBase);
        const isAnonymous = useSelector(UserSelectors.isAnonymous);
        const isMobile = EnvironmentSelectors.isMobile();
        const isPaidUser = useSelector(UserSelectors.isPaidUser);

        const forceFamilyTariffFromQuery = useFamilyTariffFromQuery();
        const showFamilyTariff = !isReferral && !isMultiTariff && (enabledFamilyTariff || forceFamilyTariffFromQuery);

        const { showSwitch, product, onClickSwitch, switchChecked, switchOptions, yearSavingDiscount, monthSavingDiscount } =
            useSwitchProducts({
                products,
                activeTab,
                withSwitch: false,
            });
        const monthDiscountPercent = crossedFullPriceInTariffCard.month && isMonthPeriod(product.period) ? monthSavingDiscount : 0;

        const [_, discount, __, productMonthPriceAsText, defaultMonthPrice, forceTrial] = usePrice({
            product,
            periodDivider: 'в',
        });
        const primaryLabel = isCheapest ? 'Тариф для Почты' : 'Выгодно';

        const isFromFamily = showFamilyTariff && tariffId === familyTariffIds.name;
        const isMayPromo2025Tariff = mayPromo2025Enabled && tariffId === mayPromo2025YearProduct;
        const [showLabel, label, labelKind] = useTariffLabel({
            product,
            skipDefaultLabel: true,
            isRecommended:
                isRecommended &&
                !discount &&
                // tempexp_18086-next-line
                !isReferral,
            primaryLabel: isRecommended ? primaryLabel : '',
            isBase,
            showDiscount: false,
            tariffPromoLabelType,
            /* tempexp_17509-start */
            isFromLitresSecondary: isPromoQuotaWithLitress && !isPaidUser && !isRecommended,
            isFromLitresPrimary: isPromoQuotaWithLitress && !isPaidUser && isRecommended,
            /* tempexp_17509-end */
            /* TODO: Добавить универсальный параметр для акционного тарифа */
            isBlackFriday2024Tariff: blackFriday2024 && isRecommended,
            isCyberMonday2025Tariff: cyberMonday2025 && isRecommended,
            /* tempexp_18084-next-line */
            isTariffCardPersonal: tariffCardPersonal && (tariffCardPersonal === 'b' || tariffCardPersonal === 'c'),
            // tempexp_18708-next-line
            autoUploadPromo,
            // tempexp_18086-next-line
            isReferral,
            isFromFamily,
            multiTariffLabel: isMultiTariff,
            isMultiBasic,
        });

        let [featureList] = (() => {
            const [features] = getFeatures(
                product,
                true,
                true,
                /* tempexp_16800-start */
                !isMobile ? getFeaturesBySpace() : undefined,
                /* tempexp_16800-end */
                isMultiBasic,
                isMultiAdvanced
            );

            return isRebrandingQuotaLanding
                ? [features.map((feature) => (feature.key === 'family' ? { ...feature, text: 'Добавление ещё 3 участников' } : feature))]
                : [features];
        })();

        const buttonAdditionalText = useButtonAdditionalText(product, crossedFullPriceInTariffCard);

        const showCrossedPrice =
            (isYearPeriod(product.period) && crossedFullPriceInTariffCard.year && product?.discountPrice) ||
            (isMonthPeriod(product.period) && crossedFullPriceInTariffCard.month && product?.discountPrice);

        // tempexp_17371-next-line
        const isOnlyYearTariff =
            cheapTariffOnlyYearDesktop === 'b' && tariff.space.space === desktopTariffMinSize && !isHideButtonInMonthTariff;

        const onBuyClick = useBuyClick({
            isMobilePayAvailable,
            isMobilePayFromGetParam,
            isIOSPromo,
            isAndroidPromo,
            enabledMidasPayment: true,
        });

        const borderType = useTariffBorder({ quota: tariff.space.original });

        const onButtonClick = useCallback(
            (event, paySource) => {
                event.stopPropagation();

                // tempexp_18090-next-line
                onTariffClick?.();

                if (mayPromo2025Enabled && product.id === mayPromo2025YearProductId) {
                    sendTmrGoal({ id: TMR3473039, goal: 'click-trial-tariff' });
                }

                // tempexp_17360-start
                if (upsaleBonus?.variant && isMonthPeriod(product.period) && !autumnPromo && !isPaidUser) {
                    const monthProductSpace = product.space.space;
                    const bonusProductId = upsaleBonus.tariffs[monthProductSpace];
                    if (bonusProductId) {
                        renderUpsaleBonusModal({
                            monthProduct: product,
                            bonusProductId,
                        });

                        return;
                    }
                }
                // tempexp_17360-end

                if (
                    !blackFriday2024 &&
                    !cyberMonday2025 &&
                    !autumnPromo &&
                    // tempexp_18086-next-line
                    !isReferral &&
                    upsaleWithTrial?.tariffs?.length &&
                    !isPaidUser &&
                    isMonthPeriod(product.period) &&
                    !((enabledFamilyTariff || forceFamilyTariffFromQuery) && isFromFamily) &&
                    !(mayPromo2025Enabled && isMayPromo2025Tariff) &&
                    !(enabledExpensivePlanTrial && isRecommended)
                ) {
                    sendClickAnalytics({ product, paySource: 'modal_choose_tariff' });
                    openPopupHelper({
                        popupName: popupNames.UPSALE_WITH_TRIAL,
                        data: {
                            monthProduct: products.monthProduct,
                            yearProduct: products.yearProduct,
                            yearSavingDiscount,
                            activePeriod: upsaleWithTrial.activePeriod,
                            resolve: (product: Product) => {
                                // tempexp_16481-next-line
                                onBuyClick({
                                    product,
                                    paySource: 'modal_choose_tariff',
                                    space: product.space.space,
                                    skipClickAnalytics: true,
                                });
                            },
                        },
                    });

                    return;
                }

                onBuyClick({
                    product,
                    // tempexp_16481-next-line
                    space: product.space.space,
                    paySource,
                });
            },
            [
                isPaidUser,
                product.period,
                product.space.space,
                product.id,
                product.price,
                products.monthProduct,
                products.yearProduct,
                yearSavingDiscount,
                onBuyClick,
                onTariffClick,
                forceFamilyTariffFromQuery,
            ]
        );

        const onTariffCardClick: ReactEventHandler = useCallback(
            (event) => {
                const source = getPaySource('tariff_card');
                onButtonClick(event, source);
            },
            [onButtonClick]
        );

        const sendAdvantagesClickAnalytic = (e?: React.MouseEvent<HTMLDivElement, MouseEvent>) => {
            if (!e || e.target instanceof HTMLAnchorElement) {
                emitAnalyticEvent(AnalyticEventNames.TARIFF_ADVANTAGES_CLICK);
                sendQuotaGa({
                    action: 'tariff',
                    label: 'benefits_click',
                    tariff: product.id,
                    platform: 'web',
                });
            }
        };
        if (!isMobile && moreBenefitsInExpensiveTariffs === 'a') {
            featureList = featureList.map((feature) => {
                if (feature.key === 'benefits') {
                    return {
                        ...feature,
                        text: <div onClick={sendAdvantagesClickAnalytic}>{feature.text}</div>,
                    };
                }
                return feature;
            });
        }
        const hasAdditionalBenefits = checkAdditionalBenefitsInSpace(product.space.original);
        // tempexp_16800-start
        if (!isMobile && moreBenefitsInExpensiveTariffs === 'b' && hasAdditionalBenefits) {
            const clickHandler = () => {
                sendAdvantagesClickAnalytic();
                const popupHelper = openPopupHelper({
                    popupName: popupNames.DETAILED_TARIFF_BENEFITS,
                    data: {
                        product,
                        buttonText: `Купить за ${productMonthPriceAsText}`,
                        onButtonClick: (e) => {
                            onButtonClick(e, MODAL_TARIFF_BENEFITS_PAY_SOURCE);
                            popupHelper?.closeDialog();
                        },
                    },
                });
            };
            featureList = featureList.map((feature) => {
                if (feature.key === 'benefits') {
                    return {
                        ...feature,
                        text: (
                            <>
                                Дополнительно ещё
                                <br />
                                <ButtonLink
                                    onClick={clickHandler}
                                    hoverUnderline={false}
                                    fontInherit
                                    primary
                                    className={styles.benefitsLink}
                                >
                                    {getAdditionalBenefitsCountText(product)}
                                </ButtonLink>
                            </>
                        ),
                    };
                }
                return feature;
            });
        }
        const buttonInFooter = !isBase && moreBenefitsInExpensiveTariffs !== 'c';
        // tempexp_16800-end

        const { setShowTooltip, renderTooltip, tooltipRef, isProductTooltip } = useTooltip({
            isMobile,
            productSpace: product.space.original,
            isSubscription: false,
            productId: product.id,
        });

        useEffect(() => {
            /** Это сделано именно здесь для того, чтобы дожидаться рендера тарифов со всем контентом внутри
             * для корректного скролла по якорю из ссылки на лендинг единой квоты
             * В родительских элементах useEffect срабатывает слишком рано, до формирования листа тарифов */
            if (isMobile) {
                return;
            }

            setCarouselLoaded?.(true);
        }, []);

        const onIntersecting = useCallback(
            once(() => emitAnalyticEvent(AnalyticEventNames.TARIFF_SHOWN, { product })),
            [product.id]
        );

        const refIntersection = useIntersecObsrWithParams({
            onIntersecting,
            isMobile,
        });

        const { space, units } = isCheapest ? tariff.space : product.space;

        let buttonInFooterGap = 36;
        if (buttonInFooter) {
            if (moreBenefitsInExpensiveTariffs === 'b') {
                buttonInFooterGap = hasAdditionalBenefits ? 36 : 54;
            }
            if (moreBenefitsInExpensiveTariffs === 'd') {
                buttonInFooterGap = 23;
            }
        }
        if (isCheapest) {
            buttonInFooterGap = 28;
        }
        if (isRebrandingQuotaLanding) {
            buttonInFooterGap = 0;
        }
        /* tempexp_17509-start */
        const { litresTooltipRef, initLitresTooltip, setShowLitresTooltip } = useLitresTooltip(
            LITRES_TOOLTIP_ID,
            product.space.original === SPACE_LIST.tb_2
        );
        /* tempexp_17509-end */

        /* tempexp_18084-start */
        const personalImage = useMemo(() => {
            const quota = tariff.space.original;

            if (quota === SPACE_LIST.gb_256) {
                return tariffPersonal256;
            }

            if (quota === SPACE_LIST.gb_512) {
                return tariffStudy;
            }

            if (quota === SPACE_LIST.tb_1) {
                return tariffPersonal;
            }

            if (quota === SPACE_LIST.tb_2) {
                return tariffWork;
            }

            return;
        }, [tariff.space.space]);
        /* tempexp_18084-end */

        // tempexp_18086-start
        let saving;
        if (isMonthPeriod(product.period)) {
            saving = monthDiscountPercent;
        } else if (!isReferral) {
            saving = yearSavingDiscount;
        }
        // tempexp_18086-end

        const familyCardItems = [...featureList];
        const familyItems = [...featureList];

        const keyUnlimitAutoupload = 'unlimit-autoupload';

        if (showFamilyTariff) {
            familyCardItems[0] = {
                text: 'Добавление ещё 3 участников',
                key: 'family-tariff',
            };
            familyCardItems[1] = {
                text: 'Семейные папки и альбомы',
                key: 'family-folders-albums',
            };
            familyCardItems[2] = {
                text: 'Без рекламы в Почте и Облаке',
                key: 'ad',
            };
            familyCardItems.unshift({
                text: 'Безлимит для фото с телефонов',
                key: keyUnlimitAutoupload,
            });

            familyItems.unshift({
                text: AUTOUPLOAD_SHORT_FEATURE_TEXT,
                key: keyUnlimitAutoupload,
            });

            if (familyTariffOnLandingB) {
                const temp = familyItems[1];
                familyItems[1] = familyItems[3];
                familyItems[3] = temp;
            }

            if (familyTariffOnLandingC || familyTariffOnLandingD) {
                familyItems[3] = familyItems[1];
                familyItems[1] = familyItems[2];
                familyItems[2] = {
                    text: 'Установка пароля на папки',
                    key: 'pin',
                };
            }
        }

        if (autoUploadPromo && !isMultiTariff) {
            if (familyTariffOnLandingA || isReferral) {
                featureList.unshift({
                    text: AUTOUPLOAD_SHORT_FEATURE_TEXT,
                    key: keyUnlimitAutoupload,
                });
                const temp = featureList[1];
                featureList[1] = featureList[3];
                featureList[3] = temp;
            } else {
                featureList[1] = featureList[0];
                featureList[0] = {
                    text: AUTOUPLOAD_SHORT_FEATURE_TEXT,
                    key: keyUnlimitAutoupload,
                };
            }
        }

        return (
            <div
                className={classNames(styles.tariffCard, {
                    [styles.tariffCard_invisible]: !isVisible,
                    [styles.tariffCard_rebranding]: isRebrandingQuotaLanding,
                })}
                ref={useMergeRefs(litresTooltipRef, tooltipRef, refIntersection)}
            >
                {/* tempexp_18084-start */}
                {personalImage && tariffCardPersonal === 'c' && <img src={personalImage} alt="Тариф" className={styles.personalImage} />}
                {/* tempexp_18084-start */}
                {/* tempexp_16481-next-line */}
                {renderTooltip && !isFromFamily && <AutouploadTooltip ref={tooltipRef} onClose={setShowTooltip} />}
                {/* tempexp_17509-start */}
                {initLitresTooltip && <LitresTooltip ref={litresTooltipRef} onClose={setShowLitresTooltip} />}
                {/* tempexp_17509-end */}
                <TariffCard
                    onClick={noopVoid}
                    dataQa="QuotaLpTariffCard"
                    tariffId={tariffId}
                    width={ETariffCardWidth.fluid}
                    /* tempexp_18084-next-line */
                    focused={isRecommended && (!tariffCardPersonal || tariffCardPersonal === 'a')}
                    /* tempexp_17509-next-line */
                    isFromLitres={isPromoQuotaWithLitress && !isPaidUser}
                    /* tempexp_18084-next-line */
                    isFromFamilyTariff={isFromFamily}
                    isMayPromo2025Tariff={isMayPromo2025Tariff}
                    buttonPrimary={isRecommended && (!tariffCardPersonal || tariffCardPersonal === 'a')}
                    buttonDisabled={isBase}
                    buttonText={
                        <div>
                            {showCrossedPrice && (
                                <span
                                    className={classNames(styles.crossedOldPrice, {
                                        [styles.crossedOldPriceFocuced]: isRecommended,
                                    })}
                                >
                                    {defaultMonthPrice}
                                </span>
                            )}
                            {enabledExpensivePlanTrial &&
                            (product.id === expensivePlanTariffIds.year || product.id === expensivePlanTariffIds.month)
                                ? forceTrial
                                : isMayPromo2025Tariff && isYearPeriod(product.period)
                                ? 'Бесплатно'
                                : productMonthPriceAsText}
                        </div>
                    }
                    onClickButton={onTariffCardClick}
                    space={{
                        space,
                        units,
                    }}
                    featureListOptions={isFromFamily ? familyCardItems : showFamilyTariff ? familyItems : featureList}
                    showSpaceWithPlus={!isCheapest}
                    isRebrandingQuotaLanding={isRebrandingQuotaLanding}
                    compact
                    accentFeature
                    buttonPrimaryLight
                    disable={Boolean(isBase)}
                    withoutButton={Boolean(isBase)}
                    //   tempexp_16800-start
                    compactFeatureList={moreBenefitsInExpensiveTariffs === 'c' || moreBenefitsInExpensiveTariffs === 'd'}
                    stretchInfoBlock={moreBenefitsInExpensiveTariffs === 'c' || moreBenefitsInExpensiveTariffs === 'd'}
                    buttonInInfoBlock={moreBenefitsInExpensiveTariffs === 'c'}
                    //   tempexp_16800-end
                    buttonInFooter={buttonInFooter}
                    buttonInFooterGap={buttonInFooterGap}
                    buttonSize={ETariffCardSizeButtonSize.big}
                    buttonAdditionalText={
                        isMayPromo2025Tariff && isYearPeriod(product.period)
                            ? 'на 3 месяца'
                            : enabledExpensivePlanTrial &&
                              (product.id === expensivePlanTariffIds.year || product.id === expensivePlanTariffIds.month)
                            ? `далее ${product.id === expensivePlanTariffIds.year ? 'год за' : ''} ${productMonthPriceAsText}`
                            : buttonAdditionalText
                    }
                    footerText={
                        isBase ? (
                            <div>
                                <Spacing size={42} />
                                <Title level="3">Бесплатно</Title>
                                <Spacing size={20} />
                            </div>
                        ) : (
                            <></>
                        )
                    }
                    infoBlock={
                        <TariffInfoBlock
                            isCheapest={isCheapest}
                            isBase={isBase}
                            newQuota={userQuota + product.space.original}
                            isSkeleton={!isAnonymous && !isQuotaLoaded}
                        />
                    }
                    newQuotaAsFeatureText={`Ваш объём составит ${bytesToNDigits(userQuota + product.space.original, 3).value}`}
                    // tempexp_18086-start
                    savingInButton={saving}
                    headerPrimaryText=""
                    {...(isRecommended
                        ? {
                              action: true,
                          }
                        : null)}
                    {...(isBase
                        ? {
                              headerPrimaryText: 'Бесплатный',
                              headerPrimaryTextLarge: true,
                              showSpaceWithPlus: false,
                          }
                        : null)}
                    {...(showSwitch
                        ? {
                              onClickSwitch,
                              switchChecked,
                              switchOptions,
                          }
                        : null)}
                    {...(showLabel
                        ? {
                              label,
                              labelKind,
                          }
                        : null)}
                    {...(isProductTooltip && { onLabelClick: setShowTooltip })}
                    {...(isPromoQuotaWithLitress && !isPaidUser && { onLabelClick: setShowLitresTooltip })}
                    hideButtonInMonthTariff={isOnlyYearTariff && activeTab === ETabsName.month}
                    borderType={borderType}
                    /* tempexp_18084-next-line */
                    withInfoBlock={!(tariffCardPersonal === 'c')}
                    mayPromo2025Enabled={mayPromo2025Enabled}
                />
            </div>
        );
    }
);

TariffPlansCard.displayName = 'TariffPlansCard';
